import { Select as MuiSelect, styled } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import { NumberTextInput } from '../../../../components/inputs/NumberTextInput'
import { colorPalette } from '../../../../style/colorPalette'

type NumberStepInputProps = {
  value: string
  onChange: (value: string) => void
  min: number
  max: number
}

const NumberStepInput = ({
  value,
  onChange,
  min,
  max,
}: NumberStepInputProps) => {
  const step = (diff: 1 | -1) => {
    const parsed = parseInt(value, 10)
    if (isNaN(parsed)) {
      onChange(String(min))
      return
    }
    onChange(String(Math.max(min, Math.min(max, parsed + diff))))
  }
  return (
    <StepControl>
      <StepButton type="button" onClick={() => step(-1)}>
        <RemoveRoundedIcon sx={{ fontSize: '15px' }} />
      </StepButton>
      <InputBox className="center" style={{ width: '52px' }}>
        <NumberTextInput
          value={value}
          onChange={e => onChange(e.target.value)}
          inputProps={{
            min,
            max,
            onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
              e.target.select(),
          }}
        />
      </InputBox>
      <StepButton type="button" onClick={() => step(1)}>
        <AddRoundedIcon sx={{ fontSize: '15px' }} />
      </StepButton>
    </StepControl>
  )
}

const FormRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const FormLabel = styled('div')({
  width: '96px',
  flexShrink: 0,
})

const StyledMuiSelect = styled(MuiSelect)({
  // 選択肢によって幅が変わらないよう固定する
  width: '150px',
  fontSize: '13px',
  backgroundColor: colorPalette.monotone[0],
})

const InputBox = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '4px 8px',
  fontSize: '13px',
  backgroundColor: colorPalette.monotone[0],
  '&:focus-within': {
    borderColor: colorPalette.skyBlue[5],
  },
  '&.error': {
    borderColor: colorPalette.pink[5],
  },
  '&.center': {
    textAlign: 'center',
  },
  '& input': {
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    width: '100%',
    padding: 0,
  },
  '&.center input': {
    textAlign: 'center',
  },
})

const StepControl = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const StepButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  border: `1px solid ${colorPalette.skyBlue[2]}`,
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  padding: 0,
  cursor: 'pointer',
  color: colorPalette.skyBlue[7],
  // アイコンの線が細く見えるため、塗りの輪郭を描いて太らせる
  '& path': {
    stroke: 'currentColor',
    strokeWidth: 1.2,
  },
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[0],
    borderColor: colorPalette.skyBlue[5],
  },
})

const ErrorText = styled('div')({
  color: colorPalette.pink[5],
  marginTop: '2px',
})

const HelperText = styled('div')({
  color: colorPalette.monotone[4],
  marginTop: '2px',
})

export {
  NumberStepInput,
  FormRow,
  FormLabel,
  StyledMuiSelect,
  InputBox,
  ErrorText,
  HelperText,
}
